import { Play, X, Filter } from 'lucide-react';

const TIME_GRANULARITIES = [
  { value: 'MONTH', label: 'Monthly' },
  { value: 'QUARTER', label: 'Quarterly' },
  { value: 'YEAR', label: 'Yearly' },
];

const FILTER_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: 'not contains' },
  { value: 'set', label: 'is set' },
  { value: 'notSet', label: 'is not set' },
];

const FALLBACK_DIMENSIONS = [
  { value: 'canonical_name', label: 'Account' },
  { value: 'cust_name', label: 'Customer' },
  { value: 'vend_name', label: 'Vendor' },
  { value: 'department', label: 'Department' },
  { value: 'class_name', label: 'Class' },
  { value: 'location_name', label: 'Location' },
  { value: 'item_name', label: 'Product/Service' },
];

// Cube dimension value → summary_by dimension token. The data-engine accepts
// summary_by as a single string ("TOTAL" | "MONTH" | "QUARTER" | "YEAR" | "BY_<dim>"),
// where <dim> is the real Cube dimension name exactly as the capabilities API
// advertises (summarize_by.dimensions[].name). The dropdown option `value` already
// IS that real dimension name, so the token is just `BY_<value>` — same convention
// the dashboard uses. The resolver passes real-name tokens through verbatim.
function dimToSummaryBy(value) {
  return `BY_${value}`;
}

export default function KpiToolbar({
  config,           // { date_range, summary_by, filters }
  onChange,          // (newConfig) => void
  onExecute,        // () => void
  loading,          // boolean
  isFormula,        // boolean — disables filters for formula KPIs
  dimensions,       // [{ value, label }] from Cube meta (or fallback)
}) {
  const dims = dimensions?.length ? dimensions : FALLBACK_DIMENSIONS;

  const updateField = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  // --- Filter row helpers ---
  const addFilter = () => {
    const current = config.filters || [];
    onChange({ ...config, filters: [...current, { dimension: '', operator: 'equals', values: [] }] });
  };

  const updateFilter = (idx, patch) => {
    const updated = (config.filters || []).map((f, i) => i === idx ? { ...f, ...patch } : f);
    onChange({ ...config, filters: updated });
  };

  const removeFilter = (idx) => {
    onChange({ ...config, filters: (config.filters || []).filter((_, i) => i !== idx) });
  };

  // summary_by is a single string. "" (select placeholder) maps to TOTAL.
  const summaryBy = config.summary_by || 'TOTAL';

  return (
    <div className="flex flex-col gap-2 p-3 border-b border-zinc-200 bg-zinc-50">
      {/* Row 1: Date range + Summary by + Execute */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-zinc-500">Date Range</label>
        <input
          type="date"
          value={config.date_range?.[0] || ''}
          onChange={e => updateField('date_range', [e.target.value, config.date_range?.[1] || ''])}
          className="bg-white text-zinc-800 text-xs rounded px-2 py-1 border border-zinc-300 focus:border-zinc-400 outline-none"
        />
        <span className="text-zinc-400 text-xs">to</span>
        <input
          type="date"
          value={config.date_range?.[1] || ''}
          onChange={e => updateField('date_range', [config.date_range?.[0] || '', e.target.value])}
          className="bg-white text-zinc-800 text-xs rounded px-2 py-1 border border-zinc-300 focus:border-zinc-400 outline-none"
        />

        <label className="text-xs text-zinc-500 ml-4">Summary By</label>
        <select
          value={summaryBy}
          onChange={e => updateField('summary_by', e.target.value)}
          className="bg-white text-zinc-800 text-xs rounded px-2 py-1 border border-zinc-300"
        >
          <option value="TOTAL">None</option>
          <optgroup label="Time">
            {TIME_GRANULARITIES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </optgroup>
          <optgroup label="Dimension">
            {dims.map(d => <option key={d.value} value={dimToSummaryBy(d.value)}>{d.label}</option>)}
          </optgroup>
        </select>

        <button
          onClick={onExecute}
          disabled={loading || !config.date_range?.[0] || !config.date_range?.[1]}
          className="ml-auto flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 disabled:text-zinc-500 text-white text-xs font-medium rounded"
        >
          <Play size={12} />
          {loading ? 'Executing...' : 'Execute'}
        </button>
      </div>

      {/* Row 2: Filters (disabled for formula KPIs) */}
      {!isFormula && (
        <div className="flex flex-col gap-1">
          {(config.filters || []).map((f, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select
                value={f.dimension}
                onChange={e => updateFilter(idx, { dimension: e.target.value })}
                className="bg-white text-zinc-800 text-xs rounded px-2 py-1 border border-zinc-300"
              >
                <option value="">Dimension...</option>
                {dims.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <select
                value={f.operator}
                onChange={e => updateFilter(idx, { operator: e.target.value })}
                className="bg-white text-zinc-800 text-xs rounded px-1 py-1 border border-zinc-300"
              >
                {FILTER_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <input
                type="text"
                value={(f.values || []).join(', ')}
                onChange={e => updateFilter(idx, { values: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
                placeholder="value1, value2"
                className="flex-1 bg-white text-zinc-800 text-xs rounded px-2 py-1 border border-zinc-300"
              />
              <button onClick={() => removeFilter(idx)} className="text-zinc-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          ))}
          <button onClick={addFilter} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 self-start">
            <Filter size={12} /> Add filter
          </button>
        </div>
      )}
    </div>
  );
}
